import React, { createContext, useContext, ReactNode, useEffect } from "react";
import {
  useProfile,
  useUpdateProfile,
} from "@/hooks/domains/profiles/useProfile";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useUserProfileRefresh } from "./UserProfileRefreshContext";
import type { Profile } from "@/types/profiles";
import type { ProfileUpdate } from "@/services/supabase/profiles";

interface ProfileContextType {
  profile: Profile | null;
  isLoading: boolean;
  isError: boolean;
  error: any;
  updateProfile: (updates: ProfileUpdate) => void;
  isUpdating: boolean;
  refetch: () => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
  profileId?: string; // Optional - if not provided, uses current user's profile
}

export function ProfileProvider({ children, profileId }: ProfileProviderProps) {
  const { user } = useAuth();
  const { triggerRefresh } = useUserProfileRefresh();
  const targetId = profileId || user?.id;

  const {
    data: profile,
    isLoading,
    isError,
    error,
    refetch,
  } = useProfile(targetId);

  const updateProfileMutation = useUpdateProfile();

  const handleUpdateProfile = (updates: ProfileUpdate) => {
    if (targetId) {
      updateProfileMutation.mutate({ id: targetId, updates });
    }
  };

// Only show loading if we don't have profile data and we're actually loading
  const effectiveIsLoading = !profile && isLoading;

  // Trigger header avatar refresh when profile update is successful
  useEffect(() => {
    if (updateProfileMutation.isSuccess) {
      console.log('[ProfileContext] Profile update successful, triggering header refresh');
      triggerRefresh();
    }
  }, [updateProfileMutation.isSuccess, triggerRefresh]);

  const value: ProfileContextType = {
    profile: profile || null,
    isLoading: effectiveIsLoading,
    isError,
    error,
    updateProfile: handleUpdateProfile,
    isUpdating: updateProfileMutation.isPending,
    refetch,
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export function useProfileContext() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  return context;
}

// Convenience hook for current user's profile
export function useCurrentUserProfile() {
  return useProfileContext();
}
